import React, { useState, useEffect } from 'react';
import { X, DollarSign, FileText, Receipt, ClipboardList } from 'lucide-react';
import { API_URL, obtenerTecnicosOperativa } from '../servicios/api';
import MaterialesAviso from './MaterialesAviso';

const ModalEditarAviso = ({ isOpen, onClose, aviso, onUpdateAviso }) => {
  const [estado, setEstado] = useState('');
  const [precio, setPrecio] = useState('');
  const [fotos, setFotos] = useState([]);
  const [documentosAEnviar, setDocumentosAEnviar] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [mensajeExito, setMensajeExito] = useState(null);

  // New state variables for editing aviso details
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fechaProgramada, setFechaProgramada] = useState('');
  const [horaProgramada, setHoraProgramada] = useState('');
  const [nombreEmpresa, setNombreEmpresa] = useState(''); // Assuming this can also be edited
  const [observaciones_tecnico, setObservacionesTecnico] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState(''); // Estado para el rol del usuario
  const [tecnicosOperativa, setTecnicosOperativa] = useState([]);
  const [tecnicoOperativoId, setTecnicoOperativoId] = useState('');

  useEffect(() => {
    if (!isOpen || currentUserRole !== 'administrador') return;
    obtenerTecnicosOperativa()
      .then((respuesta) => setTecnicosOperativa(respuesta.tecnicos || []))
      .catch((errorTecnicos) => console.error('Error al cargar técnicos:', errorTecnicos));
  }, [isOpen, currentUserRole]);

  // useEffect para cargar el rol del usuario una vez al montar el componente
  useEffect(() => {
    const userData = localStorage.getItem('usuario');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUserRole(user.rol);
    }
  }, []); // Array de dependencias vacío para que se ejecute solo una vez

  useEffect(() => {
    if (aviso) {
      // Ensure estado is always a valid ENUM value
      const initialEstado = aviso.estado && ['pendiente', 'en_diagnostico', 'esperando_repuesto', 'presupuestado', 'completado', 'cancelado', 'anulado', 'documentos_enviados', 'programado', 'resuelto'].includes(aviso.estado)
        ? aviso.estado
        : 'programado'; // Default to 'programado' if invalid or empty
      setEstado(initialEstado);
      setPrecio(aviso.precio_cobrado || '');
      setFotos([]);
      setDocumentosAEnviar(aviso.documentos_a_enviar ? JSON.parse(aviso.documentos_a_enviar) : []); // Initialize documents if present
      setError(null);
      setMensajeExito(null);

      // Initialize new fields from aviso prop
      setTitulo(aviso.titulo || '');
      setDescripcion(aviso.descripcion || '');
      setNombreEmpresa(aviso.nombre_empresa || '');
      setTecnicoOperativoId(aviso.tecnico_id ? String(aviso.tecnico_id) : '');
      setObservacionesTecnico(aviso.observaciones_tecnico || ''); // Initialize observaciones_tecnico

      // Handle fecha_programada (split into date and time)
      if (aviso.fecha_programada) {
        const [datePart, timePart] = aviso.fecha_programada.split(' ');
        setFechaProgramada(datePart || '');
        setHoraProgramada(timePart ? timePart.substring(0, 5) : ''); // Get HH:MM
      } else {
        setFechaProgramada('');
        setHoraProgramada('');
      }
    }
  }, [aviso]);

  if (!isOpen) return null;

  const handleDocumentoChange = (e) => {
    const { value, checked } = e.target;
    setDocumentosAEnviar(prev => {
      if (checked) {
        return [...prev, value];
      } else {
        return prev.filter(doc => doc !== value);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    setError(null);
    setMensajeExito(null);

    if (documentosAEnviar.length > 3) {
      setError('Debes seleccionar como máximo 3 opciones de documentos a enviar.');
      setCargando(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('No autenticado. Por favor, inicia sesión.');
      setCargando(false);
      return;
    }

    const formData = new FormData();
    formData.append('id_aviso', aviso.id_aviso);
    const estadoFinal = (estado === 'completado' && documentosAEnviar.length > 0)
      ? 'documentos_enviados'
      : estado;

    formData.append('estado', estadoFinal);
    if (estado === 'completado') {
      formData.append('precio_cobrado', precio);
    }
    // Mover la lógica de fotos fuera del if de estado para que siempre se envíen
    fotos.forEach((foto, index) => {
      formData.append(`imagenes[]`, foto); // Cambiado a 'imagenes[]'
    });
    formData.append('documentos_a_enviar', JSON.stringify(documentosAEnviar));

    // Add new fields to formData
    formData.append('titulo', titulo);
    formData.append('descripcion', descripcion);
    formData.append('fecha_programada', `${fechaProgramada} ${horaProgramada}:00`); // Combine date and time
    formData.append('nombre_empresa', nombreEmpresa);
    formData.append('observaciones_tecnico', observaciones_tecnico);
    formData.append('tecnico_id', tecnicoOperativoId);

    console.log("Enviando petición de actualización:", {
        id_aviso: aviso.id_aviso,
        estado: estadoFinal,
        precio_cobrado: precio,
        documentos_a_enviar: documentosAEnviar,
        titulo: titulo,
        descripcion: descripcion,
        fecha_programada: `${fechaProgramada} ${horaProgramada}:00`,
        nombre_empresa: nombreEmpresa,
        observaciones_tecnico: observaciones_tecnico // Asegúrate de que este estado exista y se inicialice
    });
    try {
      const respuesta = await fetch(`${API_URL}/actualizar_aviso.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const datos = await respuesta.json();

      if (datos.exito) {
        setMensajeExito(datos.mensaje);
        onUpdateAviso({ ...aviso, estado: estadoFinal }); 
        setTimeout(onClose, 1500);
      } else {
        setError(datos.mensaje);
      }
    } catch (err) {
      console.error('Error al actualizar aviso:', err);
      setError('Error de conexión con el servidor al actualizar aviso.');
    } finally {
      setCargando(false);
    }
  };

  const handleFileChange = (e) => {
    const nuevasFotos = Array.from(e.target.files || []);
    setFotos((actuales) => {
      const clavesExistentes = new Set(
        actuales.map((foto) => `${foto.name}-${foto.size}-${foto.lastModified}`)
      );
      return [
        ...actuales,
        ...nuevasFotos.filter((foto) => {
          const clave = `${foto.name}-${foto.size}-${foto.lastModified}`;
          if (clavesExistentes.has(clave)) return false;
          clavesExistentes.add(clave);
          return true;
        }),
      ];
    });
    e.target.value = '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md border border-slate-700 relative max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-4 border-b border-slate-700 p-6 pb-4">
          <h2 className="text-2xl font-bold text-white">Editar Aviso #{aviso?.numero_aviso ?? aviso?.id_aviso}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
        <form id="editar-aviso-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="estado" className="block text-slate-300 text-sm font-medium mb-1">Estado</label>
            <select
              id="estado"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="programado">Programado</option> {/* Added this line */}
              <option value="pendiente">Pendiente</option>
              <option value="en_diagnostico">En diagnóstico</option>
              <option value="esperando_repuesto">Esperando repuesto</option>
              <option value="presupuestado">Presupuestado</option>
              <option value="documentos_enviados">Documentos enviados</option>
              <option value="completado">Completado</option>
              <option value="cancelado">Cancelado</option>
              <option value="anulado">Anulado</option>
              <option value="resuelto">Resuelto</option>
            </select>
          </div>

          {currentUserRole === 'administrador' && <div>
            <label htmlFor="tecnicoOperativoAviso" className="block text-slate-300 text-sm font-medium mb-1">Técnico de taller</label>
            <select
              id="tecnicoOperativoAviso"
              value={tecnicoOperativoId}
              onChange={(e) => setTecnicoOperativoId(e.target.value)}
              className="w-full rounded-md border border-slate-600 bg-slate-700 p-2 text-white"
            >
              <option value="">Sin asignar</option>
              {tecnicosOperativa.map((tecnico) => (
                <option key={tecnico.id} value={tecnico.id}>{tecnico.nombre}</option>
              ))}
            </select>
          </div>}

          {/* New fields for editing aviso details */}
          <div>
            <label htmlFor="titulo" className="block text-slate-300 text-sm font-medium mb-1">Título</label>
            <input
              type="text"
              id="titulo"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="descripcion" className="block text-slate-300 text-sm font-medium mb-1">Descripción</label>
            <textarea
              id="descripcion"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500 h-20"
            ></textarea>
          </div>
          <div>
            <label htmlFor="fechaProgramada" className="block text-slate-300 text-sm font-medium mb-1">Fecha Programada</label>
            <input
              type="date"
              id="fechaProgramada"
              value={fechaProgramada}
              onChange={(e) => setFechaProgramada(e.target.value)}
              className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="horaProgramada" className="block text-slate-300 text-sm font-medium mb-1">Hora Programada</label>
            <input
              type="time"
              id="horaProgramada"
              value={horaProgramada}
              onChange={(e) => setHoraProgramada(e.target.value)}
              className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="nombreEmpresa" className="block text-slate-300 text-sm font-medium mb-1">Empresa</label>
            <input
              type="text"
              id="nombreEmpresa"
              value={nombreEmpresa}
              onChange={(e) => setNombreEmpresa(e.target.value)}
              className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Moved Fotos de la Reparación outside of estado === 'completado' */}
          <div>
            <label htmlFor="fotos" className="block text-slate-300 text-sm font-medium mb-1">Fotos de la Reparación</label>
            <input
              type="file"
              id="fotos"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="block w-full text-sm text-slate-300
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-500 file:text-white
                hover:file:bg-blue-600
                cursor-pointer"
            />
            {fotos.length > 0 && (
              <div className="mt-2 flex items-center justify-between gap-2 text-sm text-slate-400">
                <span>{fotos.length} imagen(es) seleccionada(s). Puedes añadir más desde la galería.</span>
                <button type="button" onClick={() => setFotos([])} className="shrink-0 text-blue-300 hover:text-blue-200">
                  Quitar todas
                </button>
              </div>
            )}
          </div>

          {estado === 'completado' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="precio" className="block text-slate-300 text-sm font-medium mb-1">Precio Cobrado</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                    <DollarSign className="h-5 w-5" />
                  </span>
                  <input
                    type="number"
                    id="precio"
                    value={precio}
                    onChange={(e) => setPrecio(e.target.value)}
                    className="w-full p-2 pl-10 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: 50.00"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-slate-300 text-sm font-medium mb-1">Documentos a Enviar</label>
            <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  value="factura"
                  checked={documentosAEnviar.includes('factura')}
                  onChange={handleDocumentoChange}
                  className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                />
                <FileText className="w-4 h-4 ml-2 mr-1" /> Factura
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  value="albaran"
                  checked={documentosAEnviar.includes('albaran')}
                  onChange={handleDocumentoChange}
                  className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                />
                <Receipt className="w-4 h-4 ml-2 mr-1" /> Albarán
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  value="informe_tecnico"
                  checked={documentosAEnviar.includes('informe_tecnico')}
                  onChange={handleDocumentoChange}
                  className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                />
                <ClipboardList className="w-4 h-4 ml-2 mr-1" /> Informe Técnico
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  value="presupuesto"
                  checked={documentosAEnviar.includes('presupuesto')}
                  onChange={handleDocumentoChange}
                  className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                />
                <DollarSign className="w-4 h-4 ml-2 mr-1" /> Presupuesto
              </label>
            </div>
          </div>

          {/* Campo de Observaciones del Técnico */}
          {(currentUserRole === 'tecnico' || currentUserRole === 'administrador') && (
            <div>
              <label htmlFor="observaciones_tecnico" className="block text-slate-300 text-sm font-medium mb-1">Diagnóstico de Avería / Observaciones del Técnico</label>
              <textarea
                id="observaciones_tecnico"
                value={observaciones_tecnico}
                onChange={(e) => setObservacionesTecnico(e.target.value)}
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-blue-500 focus:border-blue-500 h-24"
                placeholder="Introduce el diagnóstico de la avería o cualquier observación relevante."
              ></textarea>
            </div>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}
          {mensajeExito && <p className="text-green-400 text-sm">{mensajeExito}</p>}

        </form>
        <div>
          <MaterialesAviso avisoId={aviso?.id_aviso} />
        </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-700 bg-slate-800 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={cargando}
            className="rounded-md bg-slate-600 px-4 py-2 font-semibold text-white hover:bg-slate-500 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="editar-aviso-form"
            className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={cargando}
          >
            {cargando ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalEditarAviso;
